/**
 * The GLOBAL unresolved webhook-failure count (#2005).
 *
 * App-level rather than feature-level, and that is a correction rather than a
 * convenience: two features read this number now — `features/admin` for the
 * purge confirm copy, `features/webhooks` for the badge on the Failures tab —
 * and it lived inside one of them. Reaching across would have closed a barrel
 * cycle (`features/admin` already imports `WebhooksTabs` from
 * `features/webhooks`), which is exactly what the feature-barrel guardrail
 * exists to stop. Nothing about a count endpoint is admin-specific.
 *
 * It is deliberately NOT the list's own total: the list total moves with the
 * operator's filters, so a provider filter would make the badge understate the
 * backlog. One query key means both readers see one number.
 */
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import { webhookFailuresAPI, type WebhookFailureCountResponse } from '@/services/webhook-failures';

/** 5 seconds — the inbox is operator-actionable so freshness matters. */
const COUNT_STALE_MS = 5_000;
/** 30s focused-tab refetch; quiet in the background. */
const BACKGROUND_REFETCH_MS = 30_000;

export function useWebhookFailuresCount(options?: { enabled?: boolean }) {
  return useQuery<WebhookFailureCountResponse, Error>({
    // The count endpoint takes no params (global OPEN total); key it with an
    // empty record so the factory's `Record<string, unknown>` signature is
    // satisfied.
    queryKey: queryKeys.webhookFailures.count({}),
    queryFn: () => webhookFailuresAPI.count(),
    enabled: options?.enabled ?? true,
    staleTime: COUNT_STALE_MS,
    refetchInterval: BACKGROUND_REFETCH_MS,
    refetchIntervalInBackground: false,
  });
}
